import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IngredientTraceCode } from '../../../types/ingredientTraceCode';
import { Supplier } from '../../../types/supplier';
import { ShipmentEntry } from '../../../types/shipment';
import { Recipe } from '../../../types/recipe';
import { TrackingEntryRow } from './TrackingEntryRow';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchIngredientTraceCodeUsage, fetchLotNumberMap } from '../../../store/slices/ingredientTraceCodesSlice';
import { groupItemsBySection, getDefaultExpandedState } from '../../../utils/haccpSectionUtils';
import { HaccpSectionHeader } from '../../../components/haccp/HaccpSectionHeader';
import { ClickableIdentifier } from '../../../components/haccp/ClickableIdentifier';
import { useTraceability } from '../../../components/haccp/TraceabilityContext';
import { matchesSearchQuery } from '../../../utils/searchUtils';

interface TrackingTableProps {
  entries: IngredientTraceCode[];
  suppliers: Supplier[];
  shipmentEntries: ShipmentEntry[];
  fillings: Recipe[];
  components: Recipe[];
  loading: boolean;
  error: string | null;
  onAdd: (entry: IngredientTraceCode) => void;
  onUpdate: (entry: IngredientTraceCode) => void;
  onDelete: (entryId: string) => void;
  targetEntryId?: string;
  targetField?: string;
  isFlagged?: boolean;
}

export const createBlankTrackingEntry = (): IngredientTraceCode => {
  // Get local date to avoid UTC timezone shift
  const todayDate = new Date();
  const year = todayDate.getFullYear();
  const month = String(todayDate.getMonth() + 1).padStart(2, '0');
  const day = String(todayDate.getDate()).padStart(2, '0');
  const today = `${year}-${month}-${day}`;
  return {
    id: crypto.randomUUID(),
    supplierId: '',
    ingredientName: '',
    supplierLotNumber: '',
    internalTraceCode: '',
    dateUsed: '',
    quantityUsed: "",
    quantityUnit: "",
    finishedProduct: '',
    verifiedBy: '',
    comments: '',
    createdAt: new Date().toISOString(),
    ingredientReceivingId: undefined,
    dateReceived: today
  };
};

export const TrackingTable: React.FC<TrackingTableProps> = ({
  entries,
  suppliers,
  shipmentEntries,
  fillings,
  components,
  loading,
  error,
  onAdd,
  onUpdate,
  onDelete,
  targetEntryId,
  targetField,
  isFlagged
}) => {
  const dispatch = useAppDispatch();
  const { openTraceability } = useTraceability();

  // Get usage data from Redux
  const usageData = useAppSelector(state => state.ingredientTraceCodes.usageData);
  const usageLoading = useAppSelector(state => state.ingredientTraceCodes.usageLoading);
  const usageFetched = useAppSelector(state => state.ingredientTraceCodes.usageFetched);
  const lotNumberMap = useAppSelector(state => state.ingredientTraceCodes.lotNumberMap);

  // Fetch lot number map on mount for search
  useEffect(() => {
    dispatch(fetchLotNumberMap());
  }, [dispatch]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editedEntry, setEditedEntry] = useState<IngredientTraceCode | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newEntry, setNewEntry] = useState<IngredientTraceCode>(createBlankTrackingEntry());
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Section expansion state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [sectionsInitialized, setSectionsInitialized] = useState(false);

  // Filter entries based on search term
  const filteredEntries = useMemo(() => {
    if (!searchTerm.trim()) return entries;
    return entries.filter(entry => {
      const supplier = suppliers.find(s => s.id === entry.supplierId);
      const lotNumbers = lotNumberMap[entry.id] || [];
      return matchesSearchQuery(entry, searchTerm, [
        supplier?.supplierName || '',
        ...lotNumbers
      ]);
    });
  }, [entries, searchTerm, suppliers, lotNumberMap]);

  // Group filtered entries into sections by dateReceived
  const sections = useMemo(
    () => groupItemsBySection(filteredEntries, (entry) => entry.dateReceived),
    [filteredEntries]
  );

  // Initialize expanded sections state when sections change
  useEffect(() => {
    if (sections.length > 0 && !sectionsInitialized) {
      setExpandedSections(getDefaultExpandedState(sections));
      setSectionsInitialized(true);
    }
  }, [sections, sectionsInitialized]);

  // Update expanded state when new sections appear
  useEffect(() => {
    setExpandedSections((prev) => {
      const newState = { ...prev };
      let hasChanges = false;
      for (const section of sections) {
        if (!(section.key in newState)) {
          newState[section.key] = !section.isTrialSection;
          hasChanges = true;
        }
      }
      return hasChanges ? newState : prev;
    });
  }, [sections]);

  // Reset expanded rows when search term changes
  useEffect(() => {
    setExpandedRows(new Set());
  }, [searchTerm]);

  // Find which section contains the target entry for deep-linking
  const targetEntrySectionKey = useMemo(() => {
    if (!targetEntryId) return null;
    for (const section of sections) {
      if (section.items.some(e => e.id === targetEntryId)) {
        return section.key;
      }
    }
    return null;
  }, [targetEntryId, sections]);

  // Auto-expand section containing target entry for deep-linking
  useEffect(() => {
    if (targetEntrySectionKey && !expandedSections[targetEntrySectionKey]) {
      setExpandedSections(prev => ({ ...prev, [targetEntrySectionKey]: true }));
      setSearchTerm(''); // Clear search to ensure entry is visible
    }
  }, [targetEntrySectionKey]);

  // Toggle section expansion
  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const handleEdit = (entry: IngredientTraceCode) => {
    setEditingId(entry.id);
    setEditedEntry({ ...entry });
  };

  const handleSave = () => {
    if (!editedEntry) return;

    // Validate required fields (must match backend validation)
    if (!editedEntry.supplierId) {
      alert('Supplier is required.');
      return;
    }
    if (!editedEntry.ingredientName || editedEntry.ingredientName.trim() === '') {
      alert('Ingredient Name is required.');
      return;
    }
    if (!editedEntry.internalTraceCode || editedEntry.internalTraceCode.trim() === '') {
      alert('Internal Trace Code is required.');
      return;
    }

    onUpdate(editedEntry);
    setEditingId(null);
    setEditedEntry(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditedEntry(null);
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setNewEntry(createBlankTrackingEntry());
  };

  const handleSaveNew = () => {
    // Validate required fields (must match backend validation)
    if (!newEntry.supplierId) {
      alert('Supplier is required.');
      return;
    }
    if (!newEntry.ingredientName || newEntry.ingredientName.trim() === '') {
      alert('Ingredient Name is required.');
      return;
    }
    if (!newEntry.internalTraceCode || newEntry.internalTraceCode.trim() === '') {
      alert('Internal Trace Code is required.');
      return;
    }

    onAdd(newEntry);
    setIsAddingNew(false);
    setNewEntry(createBlankTrackingEntry());
  };

  const handleCancelNew = () => {
    setIsAddingNew(false);
    setNewEntry(createBlankTrackingEntry());
  };

  const handleDelete = (entryId: string) => {
    const confirmed = window.confirm('Delete this tracking entry? This cannot be undone.');
    if (confirmed) {
      onDelete(entryId);
    }
  };

  // Handler for when a row becomes visible
  const handleRowVisibility = useCallback(
    (entryId: string, isVisible: boolean) => {
      if (isVisible && !usageFetched[entryId] && !usageLoading[entryId]) {
        dispatch(fetchIngredientTraceCodeUsage(entryId));
      }
    },
    [dispatch, usageFetched, usageLoading]
  );

  const toggleRowExpansion = useCallback(
    (entryId: string) => {
      console.log('[TrackingTable] toggleRowExpansion called with entryId:', entryId);
      const newExpandedRows = new Set(expandedRows);

      if (expandedRows.has(entryId)) {
        // Collapse the row
        console.log('[TrackingTable] Collapsing row:', entryId);
        newExpandedRows.delete(entryId);
        setExpandedRows(newExpandedRows);
      } else {
        // Expand the row
        console.log('[TrackingTable] Expanding row:', entryId);
        newExpandedRows.add(entryId);
        setExpandedRows(newExpandedRows);

        // Fetch usage if not already fetched
        console.log('[TrackingTable] Checking if usage needs to be fetched:', {
          entryId,
          usageFetched: usageFetched[entryId],
          usageLoading: usageLoading[entryId]
        });
        if (!usageFetched[entryId] && !usageLoading[entryId]) {
          console.log('[TrackingTable] Dispatching fetchIngredientTraceCodeUsage for:', entryId);
          dispatch(fetchIngredientTraceCodeUsage(entryId));
        } else {
          console.log('[TrackingTable] Skipping fetch - already fetched or loading');
        }
      }
    },
    [expandedRows, usageFetched, usageLoading, dispatch]
  );

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s'
  };

  return (
    <div>
      {/* Error Message */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: '16px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: '#c33'
          }}
        >
          Error: {error}
        </div>
      )}

      {/* Add Ingredient Button */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={handleAddNew}
          disabled={isAddingNew || editingId !== null || loading}
          style={{
            ...buttonStyle,
            backgroundColor: isAddingNew || editingId !== null || loading ? '#cccccc' : '#4CAF50',
            color: 'white',
            cursor: isAddingNew || editingId !== null || loading ? 'not-allowed' : 'pointer'
          }}
          onMouseEnter={e => {
            if (!isAddingNew && editingId === null && !loading) {
              e.currentTarget.style.backgroundColor = '#45a049';
            }
          }}
          onMouseLeave={e => {
            if (!isAddingNew && editingId === null && !loading) {
              e.currentTarget.style.backgroundColor = '#4CAF50';
            }
          }}
        >
          + Track Ingredient
        </button>
      </div>

      {/* Search Input */}
      <div style={{ marginBottom: '16px' }}>
        <input
          type="text"
          placeholder="Search by ingredient, supplier, trace code, batch lot number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={loading || isAddingNew || editingId !== null}
          style={{
            width: '100%',
            padding: '12px 16px',
            fontSize: '14px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Table */}
      <div
        style={{
          border: '1px solid #ddd',
          borderRadius: '8px',
          backgroundColor: 'white',
          overflow: 'hidden'
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'center',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555',
                    width: '40px'
                  }}
                >

                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Name
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Supplier
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Supplier Lot #
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Internal Trace Code
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Date Used
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Quantity Used
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Finished Product
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Verified By
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Comments
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Usage
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'center',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555',
                    width: '100px'
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Loading State */}
              {loading && (
                <tr>
                  <td
  colSpan={12}
                    style={{
                      padding: '32px',
                      textAlign: 'center',
                      color: '#666'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <div
                        style={{
                          width: '20px',
                          height: '20px',
                          border: '3px solid #f3f3f3',
                          borderTop: '3px solid #4CAF50',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}
                      />
                      Loading...
                    </div>
                    <style>{`
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                    `}</style>
                  </td>
                </tr>
              )}

              {/* New Entry Row */}
              {!loading && isAddingNew && (
                <>
                  <tr>
                    <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}></td>
                    <td colSpan={9} style={{ padding: 0, border: 'none' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          <TrackingEntryRow
                            entry={newEntry}
                            suppliers={suppliers}
                            shipmentEntries={shipmentEntries}
                            fillings={fillings}
                            components={components}
                            isEditMode={true}
                            onChange={setNewEntry}
                            onDelete={handleCancelNew}
                          />
                        </tbody>
                      </table>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={11} style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={handleSaveNew}
                          style={{
                            ...buttonStyle,
                            backgroundColor: '#4CAF50',
                            color: 'white'
                          }}
                          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
                          onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelNew}
                          style={{
                            ...buttonStyle,
                            backgroundColor: '#757575',
                            color: 'white'
                          }}
                          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#616161')}
                          onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#757575')}
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                </>
              )}

              {/* Empty State */}
              {!loading && entries.length === 0 && !isAddingNew && (
                <tr>
                  <td
  colSpan={12}
                    style={{
                      padding: '32px',
                      textAlign: 'center',
                      color: '#999',
                      fontStyle: 'italic'
                    }}
                  >
                    No tracking entries yet. Click "Track Ingredient" to add one.
                  </td>
                </tr>
              )}
              {!loading && entries.length > 0 && filteredEntries.length === 0 && !isAddingNew && (
                <tr>
                  <td
  colSpan={12}
                    style={{
                      padding: '32px',
                      textAlign: 'center',
                      color: '#999',
                      fontStyle: 'italic'
                    }}
                  >
                    No entries match your search for "{searchTerm}".
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sections with entries */}
      {!loading && sections.map(section => {
        const isSectionExpanded = expandedSections[section.key] ?? !section.isTrialSection;

        return (
          <div key={section.key} id={`itc-section-${section.key}`} style={{ marginTop: '16px' }}>
            <HaccpSectionHeader
              label={section.label}
              itemCount={section.items.length}
              isExpanded={isSectionExpanded}
              isTrialSection={section.isTrialSection}
              onToggle={() => toggleSection(section.key)}
            />

            {isSectionExpanded && (
              <div
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  overflow: 'hidden'
                }}
              >
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '14px'
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'center', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555', width: '40px' }}></th>
                        <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Name</th>
                        <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Supplier</th>
                        <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Supplier Lot #</th>
                        <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Internal Trace Code</th>
                        <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Date Used</th>
                        <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Quantity Used</th>
                        <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Finished Product</th>
                        <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Verified By</th>
                        <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Comments</th>
                        <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Usage</th>
                        <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'center', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555', width: '100px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.items.map(entry => {
                const isEditing = editingId === entry.id;
                const displayEntry = isEditing && editedEntry ? editedEntry : entry;
                const isExpanded = expandedRows.has(entry.id);
                const records = usageData[entry.id] || [];
                const isLoadingRecords = usageLoading[entry.id] || false;
                const usageCount = usageFetched[entry.id] ? records.length : undefined;

                return (
                  <React.Fragment key={entry.id}>
                    <tr id={`itc-entry-${entry.id}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      {/* Expand/Collapse Button */}
                      <td style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                        {!isEditing && (
                          <button
                            onClick={() => {
                              console.log('[TrackingTable] Expand button clicked for entry:', entry.id);
                              toggleRowExpansion(entry.id);
                            }}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '16px',
                              color: '#666'
                            }}
                            title={isExpanded ? 'Collapse' : 'Expand usage history'}
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>
                        )}
                      </td>
                      {/* Rest of the row content */}
                      <td colSpan={10} style={{ padding: 0, border: 'none' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <tbody>
                            <TrackingEntryRow
                              entry={displayEntry}
                              suppliers={suppliers}
                              shipmentEntries={shipmentEntries}
                              fillings={fillings}
                              components={components}
                              isEditMode={isEditing}
                              onChange={setEditedEntry}
                              onDelete={() => handleDelete(entry.id)}
                              usageCount={usageCount}
                              isUsageLoading={isLoadingRecords}
                              onVisibilityChange={(isVisible) => handleRowVisibility(entry.id, isVisible)}
                              isHighlighted={entry.id === targetEntryId && !targetField}
                              targetField={entry.id === targetEntryId ? targetField : undefined}
                              isFlagged={entry.id === targetEntryId ? isFlagged : undefined}
                              usageRecords={records}
                              usageFetched={!!usageFetched[entry.id]}
                            />
                          </tbody>
                        </table>
                      </td>
                    </tr>
                    {!isEditing && (
                      <tr>
                        <td colSpan={11} style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                          <button
                            onClick={() => handleEdit(entry)}
                            disabled={isAddingNew || (editingId !== null && editingId !== entry.id)}
                            style={{
                              ...buttonStyle,
                              backgroundColor:
                                isAddingNew || (editingId !== null && editingId !== entry.id) ? '#cccccc' : '#2196F3',
                              color: 'white',
                              cursor:
                                isAddingNew || (editingId !== null && editingId !== entry.id)
                                  ? 'not-allowed'
                                  : 'pointer'
                            }}
                            onMouseEnter={e => {
                              if (!isAddingNew && (editingId === null || editingId === entry.id)) {
                                e.currentTarget.style.backgroundColor = '#1976D2';
                              }
                            }}
                            onMouseLeave={e => {
                              if (!isAddingNew && (editingId === null || editingId === entry.id)) {
                                e.currentTarget.style.backgroundColor = '#2196F3';
                              }
                            }}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    )}
                    {isEditing && (
                      <tr>
                        <td colSpan={11} style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={handleSave}
                              style={{
                                ...buttonStyle,
                                backgroundColor: '#4CAF50',
                                color: 'white'
                              }}
                              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
                              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancel}
                              style={{
                                ...buttonStyle,
                                backgroundColor: '#757575',
                                color: 'white'
                              }}
                              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#616161')}
                              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#757575')}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Usage Records */}
                    {isExpanded && !isEditing && (
                      <tr>
                        <td colSpan={11} style={{ padding: '0', backgroundColor: '#f9f9f9', borderBottom: '2px solid #ddd' }}>
                          <div style={{ padding: '16px', paddingLeft: '48px' }}>
                            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#555' }}>
                              Usage in Finished Products
                            </h4>
                            {isLoadingRecords && (
                              <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                                Loading usage records...
                              </div>
                            )}
                            {!isLoadingRecords && records.length === 0 && (
                              <div style={{ padding: '16px', fontStyle: 'italic', color: '#999' }}>
                                Not yet used in any finished products
                              </div>
                            )}
                            {!isLoadingRecords && records.length > 0 && (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                  <tr>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: '600', color: '#555' }}>
                                      Product
                                    </th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: '600', color: '#555' }}>
                                      Date Used
                                    </th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: '600', color: '#555' }}>
                                      Quantity Used
                                    </th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: '600', color: '#555' }}>
                                      Batch Lot Number
                                    </th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: '600', color: '#555' }}>
                                      Date of Production
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {records.map(record => (
                                    <tr key={record.id}>
                                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                        {record.productName || '-'}
                                      </td>
                                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                        {record.dateUsed ? new Date(record.dateUsed).toLocaleDateString('en-US') : '-'}
                                      </td>
                                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                        {record.quantityUsed} {record.unit}
                                      </td>
                                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                        {record.batchLotNumber ? (
                                          <ClickableIdentifier value={record.batchLotNumber} type="batchLotNumber" onClick={openTraceability} />
                                        ) : '-'}
                                      </td>
                                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                        {record.dateOfProduction ? new Date(record.dateOfProduction).toLocaleDateString('en-US') : '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
